import { trackGtagEvent } from '../track-gtag-event';
import { trackMetaEvent } from '../track-meta-event';
import { trackOpenAiEvent } from '../track-openai-event';
import type { Form } from '../../payload-types';

/**
 * The Ad Conversion settings a Form's Tracking tab and a Conversion Page share
 * (`adConversionFields`). Picked from `Form`, but a `Page` has the same fields
 * under the same names, so it satisfies this too.
 */
export type AdConversionSettings = Pick<
  Form,
  'metaPixelEventName' | 'openAiEventName' | 'googleTagEventName' | 'googleAdsConversionId' | 'googleAdsConversionLabel'
>;

/**
 * Fires the configured Ad Conversions (Meta Pixel, OpenAI Pixel, Google tag,
 * Google Ads) for one Form Submission, Booking or Conversion Page visit.
 * `eventId` is the one id, shared by every platform that de-duplicates on it:
 * Meta's `eventID`, OpenAI's `event_id` and Google Ads' `transaction_id`.
 *
 * The one list of platforms: `use-submit-form-submission.ts` calls it on a
 * Form Submission, `report-booking.ts` on a Booking and
 * `report-conversion-page-visit.ts` on a visit to a Conversion Page.
 *
 * Returns whether any platform took its event, sent or held until its tag
 * loads: `false` when nothing is configured or every event was dropped for
 * want of Marketing Consent.
 */
export function fireAdConversions(settings: AdConversionSettings, eventId: string): boolean {
  const taken: boolean[] = [];

  if (settings.metaPixelEventName) {
    taken.push(trackMetaEvent(settings.metaPixelEventName, {}, { eventID: eventId }));
  }

  if (settings.openAiEventName) {
    taken.push(trackOpenAiEvent(settings.openAiEventName, { event_id: eventId }));
  }

  if (settings.googleTagEventName) {
    taken.push(trackGtagEvent(settings.googleTagEventName));
  }

  if (settings.googleAdsConversionId && settings.googleAdsConversionLabel) {
    taken.push(trackGtagEvent('conversion', {
      send_to: `${settings.googleAdsConversionId}/${settings.googleAdsConversionLabel}`,
      transaction_id: eventId,
    }));
  }

  return taken.includes(true);
}
